import React, { useState, useEffect, useCallback } from 'react';
import { serviceAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import Loader from '../common/Loader';

const ServiceComparison = () => {
  const { isDarkMode } = useAdminTheme();
  const [allServiceLevels, setAllServiceLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('arrival');
  const [comparison, setComparison] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});

  useEffect(() => {
    loadAllServiceLevels();
  }, []);

  const loadPricingComparison = useCallback(async () => {
    try {
      const response = await serviceAPI.getPricingComparison(selectedCategory);
      setComparison(response.data);
    } catch (err) {
      console.error('Error loading pricing comparison:', err);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      loadPricingComparison();
    }
  }, [selectedCategory, loadPricingComparison]);

  const loadAllServiceLevels = async () => {
    try {
      setLoading(true);
      const response = await serviceAPI.getAllServiceLevels();
      setAllServiceLevels(response.data);
    } catch (err) {
      setError('Failed to load service levels');
      console.error('Error loading service levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const getCategoryName = (categoryId) => {
    const names = {
      arrival: 'Arrival',
      departure: 'Departure',
      transit: 'Transit'
    };
    return names[categoryId] || categoryId;
  };

  const getLevelColor = (levelId) => {
    if (isDarkMode) {
      const darkColors = {
        bronze: 'bg-yellow-900/40 text-yellow-300 border border-yellow-700',
        silver: 'bg-skyone-inset-dark text-zinc-200 border border-skyone-border-dark',
        gold: 'bg-yellow-900/50 text-yellow-200 border border-yellow-600',
        'vip-lounge': 'bg-purple-900/40 text-purple-300 border border-purple-700'
      };
      return darkColors[levelId] || 'bg-skyone-inset-dark text-zinc-200 border border-skyone-border-dark';
    }
    const colors = {
      bronze: 'bg-yellow-100 text-yellow-800',
      silver: 'bg-gray-100 text-gray-800',
      gold: 'bg-yellow-200 text-yellow-900',
      'vip-lounge': 'bg-purple-100 text-purple-800'
    };
    return colors[levelId] || 'bg-gray-100 text-gray-800';
  };

  const toggleFeatures = (levelId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [levelId]: !prev[levelId]
    }));
  };

  if (loading) {
    return <Loader isDarkMode={isDarkMode} fullScreen={true} />;
  }

  if (error) {
    return (
      <div className={`border rounded-lg p-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
      }`}>
        <p className={`transition-colors duration-300 ${
          isDarkMode ? 'text-red-300' : 'text-red-800'
        }`}>{error}</p>
        <button 
          onClick={loadAllServiceLevels}
          className={`mt-2 px-4 py-2 rounded transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-red-700 text-white hover:bg-red-600' 
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className={`text-2xl font-bold transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Service Comparison</h2>
        <div className="flex space-x-2">
          {['arrival', 'departure', 'transit'].map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : (isDarkMode 
                      ? 'bg-skyone-inset-dark text-zinc-200 hover:bg-skyone-border-dark' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
              }`}
            >
              {getCategoryName(category)}
            </button>
          ))}
        </div>
      </div>

      {/* Detailed Comparison for Selected Category */}
      {comparison && (
        <div>
          <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {comparison.category.name} Service Comparison
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {comparison.comparison.map((level, index) => (
              <div key={index} className={`border rounded-lg p-4 shadow-sm transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>{level.levelName}</h4>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(level.levelId)}`}>
                    {level.levelId}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className={`transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Base Price:</span>
                    <span className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{formatPrice(level.pricing.basePrice, level.pricing.currency)}</span>
                  </div>
                  {level.pricing.additionalFees > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Additional:</span>
                      <span className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{formatPrice(level.pricing.additionalFees, level.pricing.currency)}</span>
                    </div>
                  )}
                  <div className={`border-t pt-2 transition-colors duration-300 ${
                    isDarkMode ? 'border-skyone-border-dark' : ''
                  }`}>
                    <div className="flex justify-between">
                      <span className={`font-semibold transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>Total:</span>
                      <span className={`font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}>{formatPrice(level.pricing.totalPrice, level.pricing.currency)}</span>
                    </div>
                  </div>
                </div>

                <div className={`text-sm mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {level.description}
                </div>

                <div>
                  <h5 className={`text-xs font-semibold mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Features:</h5>
                  <ul className="space-y-1">
                    {(expandedFeatures[level.levelId] ? level.features : level.features.slice(0, 3)).map((feature, featureIndex) => (
                      <li key={featureIndex} className={`text-xs flex items-start transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        <span className={`mr-1 transition-colors duration-300 ${
                          isDarkMode ? 'text-green-400' : 'text-green-500'
                        }`}>•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                    {level.features.length > 3 && !expandedFeatures[level.levelId] && (
                      <li 
                        className={`text-xs cursor-pointer hover:underline transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                        }`}
                        onClick={() => toggleFeatures(level.levelId)}
                      >
                        +{level.features.length - 3} more features
                      </li>
                    )}
                    {level.features.length > 3 && expandedFeatures[level.levelId] && (
                      <li 
                        className={`text-xs cursor-pointer hover:underline transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                        }`}
                        onClick={() => toggleFeatures(level.levelId)}
                      >
                        Show less
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Service Levels Overview */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
          isDarkMode ? 'text-gray-100' : 'text-gray-900'
        }`}>All Service Levels</h3>
        <div className="overflow-x-auto">
          <table className={`min-w-full border rounded-lg transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <thead className={`transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
            }`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Service Type
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Level
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Base Price
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Features
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark divide-skyone-border-dark' : 'bg-white divide-gray-200'
            }`}>
              {allServiceLevels.map((service, index) => (
                <tr key={index} className={`transition-colors duration-300 ${
                  isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                }`}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    {service.categoryName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(service.levelId)}`}>
                      {service.levelName}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    {formatPrice(service.basePrice, service.currency)}
                  </td>
                  <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {service.features.length} features
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• All prices are in GBP and exclude VAT</li>
          <li>• Additional bag fees apply for excess luggage</li>
          <li>• VIP Lounge includes all bag fees</li>
          <li>• Electric golf carts available in all terminals except Terminal 5</li>
          <li>• Services subject to availability and airline restrictions</li>
        </ul>
      </div>
    </div>
  );
};

export default ServiceComparison;
